import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  REPLACEMENT_STATUS_LABELS,
  visibleServices,
  type EventListResponse,
  type OpenIssuesResponse,
  type PublicEvent,
  type ServiceDefinition,
} from '@inventohub/shared';
import { EventSummaryLines } from '../components/EventSummaryLines';
import { ServiceIcon } from '../components/ServiceIcon';
import { apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useScanQr } from '../lib/scan-qr';

export function HomePage(): React.JSX.Element {
  const { user, code } = useAuth();
  const signedIn = Boolean(user || code);
  const { openScan } = useScanQr();
  const services = useMemo(() => visibleServices(user, code), [user, code]);
  const [weekend, setWeekend] = useState<{ start: string; end: string } | null>(null);
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [issues, setIssues] = useState<OpenIssuesResponse['issues']>([]);

  useEffect(() => {
    if (!user && !code) return;
    let cancelled = false;
    void (async () => {
      try {
        const [eventsResult, issuesResult] = await Promise.allSettled([
          apiRequest<EventListResponse>(code ? '/api/events' : '/api/events?view=weekend'),
          code
            ? Promise.reject(new Error('td-skip-issues'))
            : apiRequest<OpenIssuesResponse>('/api/replacements/issues'),
        ]);
        if (cancelled) return;
        if (eventsResult.status === 'fulfilled') {
          setEvents(eventsResult.value.events);
          setWeekend(eventsResult.value.window ?? eventsResult.value.weekend);
        } else {
          setEvents([]);
        }
        if (issuesResult.status === 'fulfilled') {
          setIssues(issuesResult.value.issues);
        } else {
          setIssues([]);
        }
      } catch {
        if (!cancelled) {
          setEvents([]);
          setIssues([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, code]);

  function onService(service: ServiceDefinition): void {
    if (service.action === 'scan-qr') openScan();
  }

  return (
    <section data-testid="home-page">
      {signedIn ? (
        <div className="card" data-testid="home-services">
          <h2>Services</h2>
          <div className="service-grid">
            {services.map((service) =>
              service.action === 'scan-qr' ? (
                <button
                  key={service.id}
                  type="button"
                  className="service-tile"
                  data-testid={`service-tile-${service.id}`}
                  onClick={() => onService(service)}
                >
                  <ServiceIcon id={service.id} />
                  <span className="service-tile__label">{service.label}</span>
                </button>
              ) : (
                <Link
                  key={service.id}
                  to={service.href}
                  className="service-tile"
                  data-testid={`service-tile-${service.id}`}
                >
                  <ServiceIcon id={service.id} />
                  <span className="service-tile__label">{service.label}</span>
                </Link>
              ),
            )}
          </div>
        </div>
      ) : (
        <div className="card">
          <h2>Sign in</h2>
          <p className="meta">Use the Sign in control to open InventoHub.</p>
        </div>
      )}
      {user ? (
        <div className="card" data-testid="home-open-issues">
          <h2>Open issues</h2>
          <p className="meta">
            Open replacement requests and assets currently missing or broken. This is not a
            notification inbox — email still goes out on each triggering action.
          </p>
          {issues.length === 0 ? (
            <p className="meta" data-testid="home-open-issues-empty">
              No open issues.
            </p>
          ) : (
            <ul className="link-list">
              {issues.map((issue) => (
                <li key={`${issue.kind}-${issue.id}`} data-testid={`open-issue-${issue.id}`}>
                  <Link to={issue.href}>{issue.title}</Link>
                  <p className="meta">
                    {issue.kind === 'replacement'
                      ? REPLACEMENT_STATUS_LABELS[
                          issue.status as keyof typeof REPLACEMENT_STATUS_LABELS
                        ]
                      : issue.status}{' '}
                    · {issue.detail}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
      {signedIn ? (
        <div className="card" data-testid="home-events">
          <h2>{code ? 'Your event' : 'Events in the next 7 days'}</h2>
          {weekend && !code ? (
            <p className="meta">
              Times shown in America/Chicago · {weekend.start} – {weekend.end}.
            </p>
          ) : (
            <p className="meta">Times shown in America/Chicago.</p>
          )}
          {events.length === 0 ? (
            <p className="meta" data-testid="home-events-empty">
              No events in the next 7 days.
            </p>
          ) : (
            <ul className="link-list">
              {events.map((event) => (
                <li key={event.id} data-testid={`home-event-row-${event.id}`}>
                  <Link to={`/events/${event.id}`}>{event.name}</Link>
                  <EventSummaryLines event={event} />
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}
